import { useState, useEffect } from 'react';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Button from '@cloudscape-design/components/button';
import Link from '@cloudscape-design/components/link';
import { useNavigate } from 'react-router-dom';
import { KeyIndicatorsGrid } from '../components/KeyIndicatorsGrid';
import { KeyIndicatorCardProps } from '../components/KeyIndicatorCard';
import { ThemesSection } from '../components/ThemesSection';
import { TrendCharts } from '../components/TrendCharts';
import { getInsights, refreshInsights, getTrends } from '../api/tickets';
import { InsightsResponse, TrendWeek } from '../types/ticket';

function buildIndicators(insights: InsightsResponse, trends: TrendWeek[]): KeyIndicatorCardProps[] {
  const weekCounts = insights.severityCountsLastWeek ?? {};
  const prevWeekCounts = insights.previousSeverityCountsLastWeek ?? {};

  // Calculate derived values from available data
  const highSevActive = (weekCounts['SEV_1'] ?? 0) + (weekCounts['SEV_2'] ?? 0);
  const prevHighSev = (prevWeekCounts['SEV_1'] ?? 0) + (prevWeekCounts['SEV_2'] ?? 0);

  const newThisWeek = Object.values(weekCounts).reduce((a, b) => a + b, 0);
  const prevNewThisWeek = Object.values(prevWeekCounts).reduce((a, b) => a + b, 0);

  const staleCount = insights.noCorrespondence.count;
  const prevStale = insights.previousNoCorrespondenceCount;

  // Fallback derivation from live trends data when pre-computed insights
  // fields are missing/null (e.g. stale DDB record from before a deploy).
  // Uses the last two weekly buckets so the cards agree with the charts.
  const lastWeek = trends.length >= 1 ? trends[trends.length - 1] : null;
  const priorWeek = trends.length >= 2 ? trends[trends.length - 2] : null;

  const mttr = insights.meanTimeToResolve ?? lastWeek?.meanTimeToResolve ?? null;
  const prevMttr = insights.previousMeanTimeToResolve ?? priorWeek?.meanTimeToResolve ?? undefined;

  const backlogGrowth =
    insights.backlogGrowth ??
    (lastWeek ? lastWeek.totalTickets - lastWeek.resolvedTickets : null);
  const prevBacklogGrowth =
    insights.previousBacklogGrowth ??
    (priorWeek ? priorWeek.totalTickets - priorWeek.resolvedTickets : undefined);

  return [
    {
      label: 'High-Sev Active',
      value: highSevActive,
      previousValue: prevHighSev,
      unit: 'SEV 1-2',
      description: 'Count of open SEV-1 and SEV-2 tickets requiring immediate attention.',
      status: highSevActive > 0 ? 'error' : 'success',
      higherIsBetter: false,
      trendLabel: 'week over week',
    },
    {
      label: 'Stale Tickets',
      value: staleCount,
      previousValue: prevStale,
      unit: `no reply since ${new Date(Date.now() - insights.noCorrespondence.staleDays * 86400000).toLocaleDateString()}`,
      description: `Open tickets with no team correspondence in ${insights.noCorrespondence.staleDays} days or more.`,
      status: staleCount > 0 ? 'warning' : 'success',
      higherIsBetter: false,
      trendLabel: 'week over week',
    },
    {
      label: 'New This Week',
      value: newThisWeek,
      previousValue: prevNewThisWeek,
      unit: 'tickets',
      description: 'Tickets created in the last 7 days.',
      status: 'info',
      higherIsBetter: false,
      trendLabel: 'week over week',
    },
    {
      label: 'Mean Time to Resolve',
      value: mttr ?? '—',
      previousValue: prevMttr ?? undefined,
      unit: 'days',
      description: 'Average number of days from ticket creation to resolution (last 7 days).',
      status: mttr === null ? 'info'
        : mttr > 7 ? 'error'
        : mttr > 3 ? 'warning'
        : 'success',
      higherIsBetter: false,
      trendLabel: 'week over week',
      target: { value: 3, label: '(SLA)' },
    },
    {
      label: 'Backlog Growth',
      value: backlogGrowth ?? '—',
      previousValue: prevBacklogGrowth ?? undefined,
      unit: 'this week',
      description: 'Tickets created minus tickets resolved in the last 7 days. Below zero means the backlog is shrinking; above zero means it is growing.',
      status: backlogGrowth === null ? 'info'
        : backlogGrowth <= 0 ? 'success'
        : backlogGrowth <= 5 ? 'warning'
        : 'error',
      higherIsBetter: false,
      trendLabel: 'week over week',
      target: { value: 0, label: '(stable)' },
    },
    {
      label: 'Patterns Detected',
      value: insights.themes.length,
      description: 'AI-detected recurring ticket themes over the last 8 weeks.',
      status: insights.themes.length > 0 ? 'warning' : 'success',
      higherIsBetter: false,
    },
  ];
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [insights, setInsights] = useState<InsightsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [trends, setTrends] = useState<TrendWeek[]>([]);

  useEffect(() => {
    getInsights()
      .then(setInsights)
      .catch(() => {})
      .finally(() => setLoading(false));
    getTrends()
      .then((res) => setTrends(res.weeks))
      .catch(() => {});
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshInsights();
      setTimeout(async () => {
        try {
          setInsights(await getInsights());
        } catch {}
        setRefreshing(false);
      }, 3000);
    } catch {
      setRefreshing(false);
    }
  };

  const indicators: KeyIndicatorCardProps[] = insights ? buildIndicators(insights, trends) : [];

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="refresh" loading={refreshing} onClick={handleRefresh}>
              Refresh Insights
            </Button>
          </SpaceBetween>
        }
        description={insights?.generatedAt ? `Last updated: ${new Date(insights.generatedAt).toLocaleString()}` : undefined}
      >
        Dashboard
      </Header>

      <KeyIndicatorsGrid indicators={indicators} loading={loading} />

      <TrendCharts />

      <ThemesSection
        themes={insights?.themes ?? []}
        onGenerate={handleRefresh}
        generating={refreshing}
      />

      {/* Quick Links */}
      <Container header={<Header variant="h2">Quick Access</Header>}>
        <SpaceBetween direction="horizontal" size="l">
          <Link onFollow={(e) => { e.preventDefault(); navigate('/tickets'); }} fontSize="heading-m">
            View All Tickets →
          </Link>
          <Link onFollow={(e) => { e.preventDefault(); navigate('/tickets?severity=high'); }} fontSize="heading-m">
            High-Sev Tickets →
          </Link>
          <Link onFollow={(e) => { e.preventDefault(); navigate('/reports'); }} fontSize="heading-m">
            Reports →
          </Link>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}
